/**
 * mapStyles.ts
 * Стили для features регионов и округов
 **/

import type {FeatureLike} from "ol/Feature";
import type OLMap from "ol/Map";
import {Fill, Stroke, Style} from "ol/style";
import {getArea} from "ol/extent";
import {
  getCenterPoint,
  getRegionColor,
  getRegionValues,
  getMinZoomForCircle
} from "@/components/modules/MainMap/utils/mapCalculation";
import {
  DISTRICT_CENTER_OVERRIDES,
  REGION_CENTER_OVERRIDES
} from "@/components/modules/MainMap/utils/mapConstants";
import {createDataSquareStyle} from "@/components/modules/MainMap/utils/mapCanvas";
import type {ComputedRef, Ref} from "vue";
import type {MapDisplayParamsKey} from "@/composables/constants/params";

/**
 * Создание стиля для округа
 * @param districtDataMap - Данные с округами
 * @param selectedParamName - Выбранный параметр
 */
export const createDistrictStyleFunction = (
    districtDataMap: ComputedRef,
    selectedParamName: MapDisplayParamsKey
) => {
  return (feature: FeatureLike): Style[] => {
    const districtName = feature.get("name_ru");
    const districtId = feature.get("id");
    let fillColor = "#C8D7E4";

    if (districtId) {
      const districtKey = districtId.toLowerCase();
      const districtData = districtDataMap.value.get(districtKey);

      if (districtData) {
        const values = getRegionValues(districtData, selectedParamName, false);
        fillColor = getRegionColor(values.percentValue, false);
      }
    }

    const polygonStyle = new Style({
      stroke: new Stroke({
        color: "#373D3F4D",
        width: 1
      }),
      fill: new Fill({
        color: fillColor
      })
    });

    const styles = [polygonStyle];

    if (districtId) {
      const districtKey = districtId.toLowerCase();
      const districtData = districtDataMap.value.get(districtKey);

      if (districtData && DISTRICT_CENTER_OVERRIDES[districtKey]) {
        const centerPoint = getCenterPoint(districtKey, null, true);
        if (!centerPoint) return styles;

        const values = getRegionValues(districtData, selectedParamName, false);

        const squareStyles = createDataSquareStyle(
            centerPoint,
            values.absoluteValue,
            values.percentValue,
            districtName,
            "600 16px Roboto",
            -40
        );

        styles.push(...squareStyles);
      }
    }

    return styles;
  };
};

/**
 * Создание стиля региона
 * @param regionDataMap - Данные региона
 * @param selectedParamName - Наименование выбранного параметра
 * @param forecastStatusDisplay - Состояние прогноза
 * @param map - Экземпляр карты
 */
export const createRegionStyleFunction = (
    regionDataMap: ComputedRef,
    selectedParamName: MapDisplayParamsKey,
    forecastStatusDisplay: Ref<boolean>,
    map: OLMap | null
) => {
  return (feature: FeatureLike): Style[] => {
    const regionName = feature.get("name_ru");
    const regionId = feature.get("id");
    let fillColor = "#C8D7E4";

    if (regionId) {
      const regionKey = regionId.toLowerCase();
      const regionData = regionDataMap.value.get(regionKey);

      if (regionData) {
        const values = getRegionValues(regionData, selectedParamName, forecastStatusDisplay.value);
        fillColor = getRegionColor(values.percentValue, false);
      }
    }

    const polygonStyle = new Style({
      stroke: new Stroke({
        color: "#373D3F4D",
        width: 1
      }),
      fill: new Fill({
        color: fillColor
      })
    });

    const styles = [polygonStyle];

    if (regionId && map) {
      const regionKey = regionId.toLowerCase();
      const regionData = regionDataMap.value.get(regionKey);

      if (regionData) {
        const geometry = feature.getGeometry();
        if (!geometry) return styles;

        const extent = geometry.getExtent();
        const regionArea = getArea(extent);
        const minZoomForCircle = getMinZoomForCircle(regionArea);
        const currentZoom = map.getView().getZoom() || 3;

        if (currentZoom >= minZoomForCircle) {
          const hasOverride = !!REGION_CENTER_OVERRIDES[regionKey];
          const centerPoint = getCenterPoint(regionKey, extent, false);

          if (!centerPoint) return styles;

          const values = getRegionValues(regionData, selectedParamName, forecastStatusDisplay.value);

          const squareStyles = createDataSquareStyle(
              centerPoint,
              values.absoluteValue,
              values.percentValue,
              regionName,
              hasOverride ? "600 14px Roboto" : "600 12px Roboto",
              hasOverride ? -40 : -35
          );

          styles.push(...squareStyles);
        }
      }
    }

    return styles;
  };
};
